import { Injectable } from "@angular/core";
import {
  PRIMITIVE,
  ServiceMessageBrokerFactory,
  ServiceMessageBroker,
} from "shared/provider-webworker";

import { CcyPairLookupService } from "./CcyPairLookupService";
import { PrimaryLookupService } from "../../impl/PrimaryLookupService";
import { ICcyPairPromiseLookupService } from "../ICcyPairPromiseLookupService";
import { BaseLookupService } from "../../impl/BaseLookupService";
import { PromiseLookupService } from "../../impl/PromiseLookupService";

import { CubiqWorker } from "../../../worker/CubiqWorker";

import * as CubiqTS from "shared/cubiq-model-multi-asset";
import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

@Injectable()
export class CcyPairPrimaryLookupService
  extends PrimaryLookupService<string, CubiqTS.CurrencyPairImpl>
  implements ICcyPairPromiseLookupService
{
  constructor(
    protected base: CcyPairLookupService,
    protected factory: ServiceMessageBrokerFactory,
    protected cubiqWorker: CubiqWorker,
  ) {
    super(base, factory, CubiqTS.CurrencyPairImpl, cubiqWorker, "CurrencyPairImpl");

    cubiqWorker.isAuth(`${this.constructor.name}`).then(
      () => {
        let ccyPairsObservable = cubiqWorker.primeStaticData<CubiqTS.CurrencyPairImpl>(
          CubiqTS.CurrencyPairImpl.name,
        );

        ccyPairsObservable.subscribe(
          (ccyPairs: CubiqTS.CurrencyPairImpl[]) => {
            this.base.prime(ccyPairs);
          },
          (error: Error) => {
            this._primed.reject(error);
            console.debug("CurrencyPair prime failed");
          },
          () => {
            this._primed.resolve(true);
          },
        );
      },
      (reason) => {
        console.debug("CcyPairPrimaryLookupService: Failed to prime: " + reason);

        this._primed.resolve(false);
      },
    );

    this.broker.registerMethod(
      "getCcyPair",
      [PRIMITIVE, PRIMITIVE],
      this.getCcyPair.bind(this),
      this.VALUE,
    );
    this.broker.registerMethod(
      "getVehicleCurrencies",
      [PRIMITIVE, PRIMITIVE],
      this.getVehicleCurrencies.bind(this),
      PRIMITIVE,
    );
  }

  getCcyPair(base: string, nonBase: string): Promise<CubiqTS.CurrencyPairImpl> {
    return new Promise<CubiqTS.CurrencyPairImpl>((res, rej) => {
      res(this.base.getCcyPair(base, nonBase));
    });
  }

  getVehicleCurrencies(base: string, nonBase: string): Promise<Array<any>> {
    return new Promise<any>((res, rej) => {
      let vehicles: Array<any> = [];
      if (base != "EUR" && nonBase != "EUR") {
        let crosses = this.getCrosses(base, nonBase, "EUR");
        if (crosses) {
          vehicles.push({ label: "EUR", value: "EUR", crosses: crosses });
        }
      }
      if (base != "USD" && nonBase != "USD") {
        let crosses = this.getCrosses(base, nonBase, "USD");
        if (crosses) {
          vehicles.push({ label: "USD", value: "USD", crosses: crosses });
        }
      }
      res(vehicles);
    });
  }

  private getCrosses(base: string, nonBase: string, cross: string): Array<any> {
    let leg1 = this.base.getCcyPair(base, cross);
    let leg2 = this.base.getCcyPair(nonBase, cross);

    if (leg1 && leg2) {
      return [this.adaptCcyPair(leg1), this.adaptCcyPair(leg2)];
    }
    return null;
  }

  private adaptCcyPair(pair: CubiqTS.CurrencyPairImpl): any {
    let minPrecision: number = Math.max(
      pair.rateDecimalsQty,
      pair.markupSptDecimals,
      pair.limitEntrySpotDecimals,
    );
    let pipPrecision: number = pair.rateDecimalsQty;
    if (pair.spotPrecision != CubiqTS.Precision.FULL) {
      minPrecision = minPrecision + 1;
    }
    return {
      base: pair.base,
      nonBase: pair.nonBase,
      minPrecision: minPrecision,
      pipPrecision: pipPrecision,
    };
  }
}
